import {Card, Col, Row} from "react-bootstrap"
import {Brush, ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts"
import React from "react"
import {DateTime} from "../../time/DateTime";
import {PowerEntry} from "../../client/DataClient";
import {CustomTooltip} from "./CustomToolTip";

interface ReadingsChartProps {
    readings, events, setBrushRange, brushRange, chartRange
}

export const ReadingsChart = ({ readings, events, setBrushRange, brushRange, chartRange }: ReadingsChartProps) => {
    const yAxisFormatter = power => {
        return power.toString() + ' MW'
    }

    return <Row><div data-testid="chart-container" className="chart-container" id="chart-container">
        <Col xs={12} className="p-0">
            <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={readings} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <XAxis
                        dataKey="date"
                        tickLine={false}
                        tick={<CustomizedAxisTick readings={readings} brushRange={brushRange} />}
                        interval={0}
                    />
                    <YAxis dataKey="power" tick={{ fill: '#666', width: '75' }}
                           axisLine={{ stroke: '#666' }}
                           tickFormatter={yAxisFormatter}
                           tickLine={false}
                    />
                    <Tooltip content={CustomTooltip} />
                    <Line
                        dataKey="power"
                        stroke="#27AE60"
                        strokeWidth={2}
                        dot={false}
                    />
                    <Line
                        dataKey="baseline"
                        stroke="#EE2222"
                        // strokeDasharray="6 4"
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                        name="Baseline"
                    />
                    <Line
                        dataKey="expected"
                        stroke="#EE2222"
                        strokeDasharray="4 4"
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                        name="Predicted Power"
                    />
                    <Brush
                        dataKey="date"
                        data-testid="chart-brush"
                        startIndex={brushRange ? brushRange[0] : 0}
                        endIndex={brushRange ? brushRange[1] : readings.length - 1}
                        height={30}
                        travellerWidth={30}
                        stroke="#27AE60"
                        onChange={({ startIndex, endIndex }) => (startIndex && endIndex) ? setBrushRange([startIndex, endIndex]) : undefined}
                        tickFormatter={() => ''}
                    />
                    {events?.map((event, index) => {
                        const eventStart = event.start
                        const eventEnd = event.end
                        const range = chartRange()
                        if (range) {
                            return <ReferenceArea
                                key={index}
                                x1={(eventStart < range.start ? range.start : eventStart).toUnixInteger()}
                                x2={(eventEnd > range.end ? range.end : eventEnd).toUnixInteger()}
                                stroke="none"
                                fill="rgba(255, 165, 0, 0.5)"
                            />
                        }
                    })}
                </ComposedChart>
            </ResponsiveContainer>
        </Col>
    </div>
    </Row>
}

export const NoDataAvailable = () =>
    <Card className={'text-center w-100 p-4 border'} style={{ border: '#ccc' }} data-testid="no-data-card">
        <Card.Body>
            <Card.Title>No Data Available</Card.Title>
            <Card.Text>
                There is no data available for this chart. Please select different parameters or try again later.
            </Card.Text>
        </Card.Body>
    </Card>

export type TickProps = {
    x?: number
    y?: number
    payload?: any
    readings?: PowerEntry[]
    brushRange?: [number, number]
}

export const CustomizedAxisTick: React.FC<TickProps> = ({ x, y, payload, readings, brushRange }) => {
    if (readings && readings.length > 0 && brushRange) {
        const value = payload.value
        const date = DateTime.fromSeconds(value).toLondonTimeZone()
        const minutes = date.minute
        const hours = date.hour
        const seconds = date.second

        if (seconds != 0) return null

        const label = () => <svg><text data-testid="x-tick" x={x} y={y} dy={16} textAnchor="middle" fill="#666">{date.toFormat("hh:mm a")}</text></svg>
        const range = readings[brushRange[1]].date - readings[brushRange[0]].date

        if (range / 60 < 6) {
            return label()
        }
        else if (range / 60 < 15) {
            return minutes % 2 === 0 ? label() : null
        }
        else if (range / 60 < 30) {
            return minutes % 5 === 0 ? label() : null
        } else if (range / 60 < 90) {
            return minutes % 15 === 0 ? label() : null
        } else if (range / 60 / 60 < 3) {
            return minutes % 30 === 0 ? label() : null
        } else if (range / 60 / 60 < 6) {
            return minutes === 0 ? label() : null
        } else if (range / 60 / 60 < 12) {
            return minutes === 0 && hours % 2 === 0 ? label() : null
        } else if (range / 60 / 60 < 18) {
            return minutes === 0 && hours % 3 === 0 ? label() : null
        } else {
            return minutes === 0 && hours % 4 === 0 ? label() : null
        }

    } else return null
}
